import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { formatPeriod } from 'app/KinerjaShared/utils/period'
import { addDaysToDate, formatDate } from 'utils/datetime'

import type { FC } from 'react'

export type CardPeriodTypeProps = {
  type?: 'all' | 'perencanaan' | 'pelaksanaan' | 'last-day'
  separator?: string
  withDate?: boolean
  withYear?: boolean
}

/**
 * Renders a card component that displays the period type.
 *
 * @component
 * @example
 * // Renders a card with the period type set to 'all'
 * <CardPeriodType type="all" separator=" - " withDate={false} withYear={false} />
 *
 * @param {Object} props - The component props.
 * @param {string} [props.type='all'] - The type of period. Possible values are 'all', 'last-day', 'perencanaan', or 'pelaksanaan'.
 * @param {string} [props.separator=' - '] - The separator to be used between the start and end dates.
 * @param {boolean} [props.withDate=false] - Determines whether to include the date in the period. Will be set to true if the type is 'perencanaan' or 'last-day'.
 * @param {boolean} [props.withYear=false] - Determines whether to include the year in the period.
 * @returns {JSX.Element} The rendered card component.
 */
const CardPeriodType: FC<CardPeriodTypeProps> = ({
  type = 'all',
  separator = ' - ',
  withDate = false,
  withYear = false,
}) => {
  // Check if the date should be included
  const shouldIncludeDate = type === 'perencanaan' || withDate

  // Get the period data from the context
  const { period: periodData } = usePeriodContext()

  // Get the start and end dates
  let [start, end] = [periodData.startDate, periodData.endDate]

  // Return an empty fragment if the period data is empty
  if (periodData?.periodeKinerja?.length === 0) return <></>

  // Return the last day of the period
  if (type === 'last-day') {
    const dateFullFormats = [
      ...(withDate ? ['d'] : []),
      'LLLL',
      ...(withYear ? ['yyyy'] : []),
    ]
    return <>{formatDate(end, dateFullFormats.join(' '))}</>
  }

  // Set the start and end dates based on the period type for the perencanaan period
  if (type === 'perencanaan') {
    start = periodData?.periodeKinerja?.[0]?.value
    end = addDaysToDate(periodData?.periodeKinerja?.[1]?.value, -1)
  }

  // Set the start and end dates based on the period type for the pelaksanaan period
  else if (type === 'pelaksanaan') {
    start = periodData?.periodeKinerja?.[1]?.value
  }

  // Return the formatted period
  return (
    <>
      {formatPeriod(start, end, {
        separator,
        withDate: shouldIncludeDate,
        withYear,
      })}
    </>
  )
}

export default CardPeriodType
